import {
  AgentCapability,
  agentHarnessId,
  isCohesiveHarness,
} from "@/constants/agents";
import {
  HARNESS_IDS,
  type CreateFlowScheduleRequest,
  type FlowSchedule,
  type HarnessId,
  type UpdateFlowScheduleRequest,
} from "@cohesive/api-client";

/** What a schedule's runs are stamped with, as the automation forms hold it. */
export interface AutomationHarnessSelection {
  /**
   * The agent's harness. Cohesive is held as `cohesive` whichever loop serves it: the server stores
   * it unpinned, so each run follows the owner's harness-runtime flag.
   */
  harness: HarnessId;
  /** Undefined means each run resolves the harness default at fire time. Never set on Cohesive. */
  model?: string;
  /**
   * Undefined means each run takes the default. Cohesive's tier on Cohesive; the model's effort
   * on a subscription.
   */
  effort?: string;
}

export const DEFAULT_AUTOMATION_HARNESS: AutomationHarnessSelection = {
  harness: HARNESS_IDS.COHESIVE,
};

/**
 * A schedule as the form holds it. Cohesive on either loop reads as `cohesive`, a schedule that
 * predates the stamp included; a model a Cohesive schedule was pinned to before tiers is dropped,
 * since Cohesive names none.
 */
export function automationHarnessOf(
  schedule: FlowSchedule,
): AutomationHarnessSelection {
  if (isCohesiveHarness(schedule.harness)) {
    return {
      harness: HARNESS_IDS.COHESIVE,
      effort: schedule.reasoning_effort || undefined,
    };
  }
  return {
    harness: schedule.harness ?? DEFAULT_AUTOMATION_HARNESS.harness,
    model: schedule.model || undefined,
    effort: schedule.reasoning_effort || undefined,
  };
}

/** The selection that runs on `agent`, at its default model and effort. */
export function automationAgentSelection(
  agent: AgentCapability,
): AutomationHarnessSelection {
  return { harness: agentHarnessId(agent) };
}

/** The selection as the server should stamp it. */
export function automationCreateFields(
  selection: AutomationHarnessSelection,
): Pick<CreateFlowScheduleRequest, "harness" | "model" | "reasoning_effort"> {
  return {
    harness: selection.harness,
    model: selection.model,
    reasoning_effort: selection.effort,
  };
}

export function automationHarnessDirty(
  saved: AutomationHarnessSelection,
  next: AutomationHarnessSelection,
): boolean {
  return (
    saved.harness !== next.harness ||
    saved.model !== next.model ||
    saved.effort !== next.effort
  );
}

/**
 * The harness half of a PATCH body.
 *
 * The model always rides an agent change: the server validates it against whichever harness
 * the row lands on, and the vocabularies don't cross, so leaving the old harness's model
 * behind is what a 400 looks like. `null` clears the pin back to the harness default —
 * omitting the key would keep it, which is the one thing a chat message cannot express. The
 * effort is sent only when it changed: the server applies the same keep-or-clear rule the form
 * does on an agent or model change.
 */
export function automationHarnessPatch(
  saved: AutomationHarnessSelection,
  next: AutomationHarnessSelection,
): Pick<UpdateFlowScheduleRequest, "harness" | "model" | "reasoning_effort"> {
  if (!automationHarnessDirty(saved, next)) return {};
  return {
    ...(next.harness !== saved.harness ? { harness: next.harness } : {}),
    model: next.model ?? null,
    ...(next.effort !== saved.effort
      ? { reasoning_effort: next.effort ?? null }
      : {}),
  };
}
